import React, { useEffect, useRef, useState } from "react";
import useOrdenarDocumentos from '../../../componentes/comuns/useOrdenarDocumentos';

import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Radio,
  RadioGroup,
  Snackbar,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  AddRounded,
  BusinessRounded,
  CancelRounded,
  CloseRounded,
  DeleteOutlineRounded,
  DescriptionRounded,
  DragIndicatorRounded,
  FolderCopy,
  InfoOutlined,
  SaveRounded,
  SchoolRounded,
  SearchRounded,
  SubjectRounded,
  TitleRounded,
} from "@mui/icons-material";

import { useNavigate } from "react-router-dom";
import { api } from "@api/api";
import { lerTipoInstituicao, montarDadosTipoProcesso } from "../../../processos/tipoInstituicao";

import {
  botaoAdicionar,
  botaoCancelar,
  botaoSalvar,
} from "../../../estilos/botoes";

import { ajustarTamanhoFonteSre } from "../../../estilos/global";

const EXIBIR_TIPO_INSTITUICAO = true;

const estiloSecao = {
  border: "1px solid var(--sear-borda, #d7e7f7)",
  borderRadius: 1.3,
  backgroundColor: "var(--sear-superficie, #fff)",
  p: 1.4,
  boxShadow: "none",
};

const estiloCabecalhoTabela = {
  bgcolor: "var(--sear-superficie-azul, #edf6fe)",
  color: "var(--sear-azul, #27456f)",
  fontSize: ajustarTamanhoFonteSre("0.74rem"),
  fontWeight: 700,
  py: 0.8,
  borderBottom: "1px solid var(--sear-borda, #d7e7f7)",
};

function TituloSecao({ icon, titulo, subtitulo, compacto = false }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        p: compacto ? 0.6 : 0.95,
        mb: compacto ? 0.65 : 1.1,
        bgcolor: "var(--sear-superficie-azul, #f0f7ff)",
        border: "1px solid var(--sear-borda, #dceaf8)",
        borderRadius: 1.1,
      }}
    >
      <Box
        sx={{
          width: compacto ? 24 : 32,
          height: compacto ? 24 : 32,
          display: "grid",
          placeItems: "center",
          bgcolor: "var(--sear-superficie-azul, #e3f1ff)",
          color: "var(--sear-azul, #087cf0)",
          borderRadius: 1,
        }}
      >
        {icon}
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            color: "var(--sear-azul, #0767d8)",
            fontSize: ajustarTamanhoFonteSre("0.86rem"),
            fontWeight: 700,
          }}
        >
          {titulo}
        </Typography>

        <Typography
          sx={{
            color: "var(--sear-texto-secundario, #71849d)",
            fontSize: ajustarTamanhoFonteSre("0.71rem"),
            display: compacto ? "none" : "block",
          }}
        >
          {subtitulo}
        </Typography>
      </Box>
    </Box>
  );
}

export default function CriarProcesso({
  onCancelar,
  onCriado,
  onAtualizado,
  modo = "criar",
  processoId = null,
} = {}) {
  const navigate = useNavigate();
  const editando = modo === "editar";

  const [original, setOriginal] = useState(null);
  const [carregando, setCarregando] = useState(editando);

  const [processo, setProcesso] = useState({
    nome: "",
    descricao: "",
    documentos: [],
  });

  // Não presumir categoria de processos antigos.
  const [tipoInstituicao, setTipoInstituicao] = useState("");

  const [abaCadastro, setAbaCadastro] = useState(0);
  const [confirmarSaida, setConfirmarSaida] = useState(false);
  const [documentosDisponiveis, setDocumentosDisponiveis] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recarregarDocumentos, setRecarregarDocumentos] = useState(0);
  const [errors, setErrors] = useState({});
  const [errorType, setErrorType] = useState(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [modalDocumentosAberto, setModalDocumentosAberto] = useState(false);
  const [buscaModal, setBuscaModal] = useState("");
  const [buscaVinculados, setBuscaVinculados] = useState("");
  const [documentosMarcados, setDocumentosMarcados] = useState([]);
  const [ordemAlterada, setOrdemAlterada] = useState(false);
  const tabelaDocumentosRef = useRef(null);
  const ordenacao = useOrdenarDocumentos({
    ids: processo.documentos, containerRef: tabelaDocumentosRef,
    desabilitado: loading || carregando || Boolean(errorType) || Boolean(buscaVinculados.trim()),
    onReordenar: (ids) => { setProcesso((atual) => ({ ...atual, documentos: ids })); setOrdemAlterada(true); },
  });

  // Reserva a largura da barra nativa fora da borda, sem faixa à esquerda.
  useEffect(() => {
    const area = tabelaDocumentosRef.current;
    if (!area) return undefined;
    const moldura = area.parentElement;
    const atualizarEspacoBarra = () => {
      if (!area.offsetWidth) return;
      const larguraBarra = Math.max(0, area.offsetWidth - area.clientWidth);
      moldura.style.setProperty("--espaco-rolagem-documentos", `${larguraBarra + 4}px`);
    };
    atualizarEspacoBarra();
    const observador = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(atualizarEspacoBarra);
    observador?.observe(area);
    window.addEventListener("resize", atualizarEspacoBarra);
    return () => {
      observador?.disconnect();
      window.removeEventListener("resize", atualizarEspacoBarra);
      moldura.style.removeProperty("--espaco-rolagem-documentos");
    };
  }, [abaCadastro, carregando, errorType]);

  // ====================================================
  // CARREGAMENTO
  // ====================================================

  useEffect(() => {
    let ativo = true;

    const buscar = async () => {
      try {
        setCarregando(true);

        const requisicoes = [
          fetch(api.adminListDocs(), {
            credentials: "include",
          }),
        ];

        if (editando) {
          if (!processoId) {
            throw new Error("ID do processo não informado.");
          }

          requisicoes.push(
            fetch(api.adminSearchProcess(processoId), {
              credentials: "include",
            }),
          );
        }

        const respostas = await Promise.all(requisicoes);

        for (const resposta of respostas) {
          if (!resposta.ok) {
            const erro = new Error(
              "Não foi possível consultar os dados do processo.",
            );
            erro.status = resposta.status;
            throw erro;
          }
        }

        const [listaDocumentos, dadosProcesso] = await Promise.all(
          respostas.map((resposta) => resposta.json()),
        );

        if (!Array.isArray(listaDocumentos)) {
          throw new Error("Resposta inválida da consulta de documentos.");
        }

        if (
          editando &&
          (!dadosProcesso ||
            dadosProcesso.error ||
            !Array.isArray(dadosProcesso.documentos))
        ) {
          throw new Error(
            dadosProcesso?.error || "Dados do processo inválidos.",
          );
        }

        if (!ativo) return;

        setDocumentosDisponiveis(listaDocumentos);

        if (editando) {
          const dados = {
            nome: String(dadosProcesso.nome ?? ""),
            descricao: String(dadosProcesso.descricao ?? ""),
            documentos: dadosProcesso.documentos.map((doc) => doc.id),
            tipo_instituicao: lerTipoInstituicao(dadosProcesso),
            versao_ordem: dadosProcesso.versao_ordem,
            ordem_documentos_sre: dadosProcesso.ordem_documentos_sre,
          };

          setProcesso(dados);
          setTipoInstituicao(dados.tipo_instituicao);
          setOriginal(dados);
          setOrdemAlterada(false);
        }

        setErrorType(null);
      } catch (erro) {
        console.error("Erro ao carregar cadastro de processo:", erro);

        if (ativo) {
          setErrorType(erro.status === 403 ? "permission" : "module");
        }
      } finally {
        if (ativo) setCarregando(false);
      }
    };

    buscar();

    return () => {
      ativo = false;
    };
  }, [editando, processoId, recarregarDocumentos]);

  // ====================================================
  // CAMPOS
  // ====================================================

  const handleChange = ({ target: { name, value } }) => {
    setProcesso((atual) => ({
      ...atual,
      [name]: value,
    }));

    setErrors((atual) => ({
      ...atual,
      [name]: "",
    }));
  };

  const documentosPorId = new Map(
    documentosDisponiveis.map((doc) => [String(doc.id), doc]),
  );

  const idsVinculados = new Set(processo.documentos.map(String));

  const correspondeBusca = (doc, termo) =>
    `${doc.nome || ""} ${doc.descricao || ""}`
      .toLocaleLowerCase("pt-BR")
      .includes(termo.trim().toLocaleLowerCase("pt-BR"));

  const documentosVinculados = processo.documentos
    .map((id) => documentosPorId.get(String(id)))
    .filter(Boolean)
    .filter((doc) => correspondeBusca(doc, buscaVinculados));

  const documentosModal = documentosDisponiveis.filter((doc) =>
    correspondeBusca(doc, buscaModal),
  );

  const elegiveisVisiveis = documentosModal.filter(
    (doc) => !idsVinculados.has(String(doc.id)),
  );

  const estaMarcado = (id) =>
    documentosMarcados.some((item) => String(item) === String(id));

  const todosVisiveisMarcados =
    elegiveisVisiveis.length > 0 &&
    elegiveisVisiveis.every((doc) => estaMarcado(doc.id));

  const algumVisivelMarcado = elegiveisVisiveis.some((doc) =>
    estaMarcado(doc.id),
  );

  // ====================================================
  // SELEÇÃO DE DOCUMENTOS
  // ====================================================

  const abrirModalDocumentos = () => {
    setBuscaModal("");
    setDocumentosMarcados([]);
    setModalDocumentosAberto(true);
  };

  const alternarDocumentoMarcado = (id, marcado) => {
    setDocumentosMarcados((atual) =>
      marcado
        ? [...atual.filter((item) => String(item) !== String(id)), id]
        : atual.filter((item) => String(item) !== String(id)),
    );
  };

  const alternarTodosVisiveis = (marcado) => {
    const visiveis = new Set(elegiveisVisiveis.map((doc) => String(doc.id)));

    setDocumentosMarcados((atual) => {
      const outros = atual.filter((id) => !visiveis.has(String(id)));

      return marcado
        ? [...outros, ...elegiveisVisiveis.map((doc) => doc.id)]
        : outros;
    });
  };

  const adicionarDocumentosSelecionados = () => {
    setProcesso((atual) => {
      const existentes = new Set(atual.documentos.map(String));

      return {
        ...atual,
        documentos: [
          ...atual.documentos,
          ...documentosMarcados.filter((id) => !existentes.has(String(id))),
        ],
      };
    });

    setErrors((atual) => ({
      ...atual,
      documentos: "",
    }));

    setModalDocumentosAberto(false);
    setDocumentosMarcados([]);
  };

  const removerDocumento = (id) => {
    setProcesso((atual) => ({
      ...atual,
      documentos: atual.documentos.filter(
        (item) => String(item) !== String(id),
      ),
    }));
  };

  // ====================================================
  // ALTERAÇÕES E VALIDAÇÃO
  // ====================================================

  const idsIguais = (a, b) =>
    a.length === b.length &&
    a.every((id, index) => String(id) === String(b[index]));

  const dadosAlterados = Boolean(
    !editando ||
    (original &&
      (tipoInstituicao !== original.tipo_instituicao ||
        processo.nome !== original.nome ||
        processo.descricao !== original.descricao ||
        !idsIguais(processo.documentos, original.documentos))),
  );

  // ====================================================
  // SALVAR
  // ====================================================

  const handleSave = async () => {
    if (loading || carregando || ordenacao.arrastando || errorType || (editando && !dadosAlterados)) {
      return;
    }

    const novosErros = {};

    if (!lerTipoInstituicao(tipoInstituicao)) {
      novosErros.tipoInstituicao = "Selecione Mantenedora ou Escola.";
    }

    if (!processo.nome.trim()) {
      novosErros.nome = "Nome do processo é obrigatório";
    }

    if (!processo.descricao.trim()) {
      novosErros.descricao = "Descrição é obrigatória";
    }

    if (!processo.documentos.length || processo.documentos.some((id) => !id)) {
      novosErros.documentos = "Adicione pelo menos um documento";
    }

    setErrors(novosErros);

    if (Object.keys(novosErros).length) {
      setAbaCadastro(novosErros.tipoInstituicao || novosErros.nome || novosErros.descricao ? 0 : 1);
      return;
    }

    try {
      setLoading(true);

      const resposta = await fetch(
        editando
          ? api.adminUpdateProcess(processoId)
          : api.adminCreateProcessType(),
        {
          credentials: "include",
          method: editando ? "PATCH" : "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(
            montarDadosTipoProcesso({ ...processo, ...(!editando || ordemAlterada || Array.isArray(original?.ordem_documentos_sre) ? { ordem_documentos: [...processo.documentos] } : {}) }, tipoInstituicao, editando),
          ),
        },
      );

      if (!resposta.ok) {
        const dados = await resposta.json().catch(() => ({}));

        throw new Error(
          dados.error || dados.message || "Erro ao salvar processo",
        );
      }

      if (editando ? onAtualizado : onCriado) {
        if (editando) {
          onAtualizado();
        } else {
          onCriado();
        }
      } else {
        setSnackbar({
          open: true,
          message: editando
            ? "Processo atualizado com sucesso!"
            : "Processo criado com sucesso!",
          severity: "success",
        });

        setTimeout(() => navigate("/admin/processo"), 1500);
      }
    } catch (erro) {
      console.error("Erro ao salvar processo:", erro);

      setSnackbar({
        open: true,
        message: erro.message || "Erro ao salvar processo",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // ====================================================
  // FECHAMENTO
  // ====================================================

  const possuiAlteracoes = editando
    ? dadosAlterados
    : Boolean(
        processo.nome.trim() ||
        processo.descricao.trim() ||
        processo.documentos.length ||
        Boolean(tipoInstituicao),
      );

  const fecharCadastro = () => {
    setConfirmarSaida(false);

    if (onCancelar) {
      onCancelar();
    } else {
      navigate("/admin/processo");
    }
  };

  const solicitarFechamento = () => {
    if (loading || modalDocumentosAberto || confirmarSaida) {
      return;
    }

    if (possuiAlteracoes) {
      setConfirmarSaida(true);
    } else {
      fecharCadastro();
    }
  };

  // ====================================================
  // TABELA DE DOCUMENTOS
  // ====================================================

  const documentosTabela = (
    <Box sx={{
      ...estiloSecao, mb: 0,
      display: { sm: "flex" }, flexDirection: "column",
      flex: { sm: "1 1 0" }, minHeight: 0,
      "& > :not(.MuiTableContainer-root)": { flexShrink: 0 },
    }}>
      <TituloSecao
        icon={<DescriptionRounded sx={{ fontSize: 19 }} />}
        titulo="Documentos necessários"
        subtitulo="Adicione os documentos que serão exigidos neste processo."
        compacto
      />

      <Box
        sx={{
          display: "flex",
          gap: 1,
          mb: 1.1,
          flexWrap: "wrap",
        }}
      >
        <TextField
          size="small"
          placeholder="Buscar documento..."
          value={buscaVinculados}
          onChange={(e) => setBuscaVinculados(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRounded
                  sx={{
                    fontSize: 17,
                    color: "var(--sear-texto-secundario, #607995)",
                  }}
                />
              </InputAdornment>
            ),
          }}
          sx={{ flex: "1 1 240px" }}
        />

        <Button
          size="small"
          variant="contained"
          startIcon={<AddRounded />}
          onClick={abrirModalDocumentos}
          disabled={loading}
          sx={botaoAdicionar}
        >
          Adicionar documento
        </Button>
      </Box>

      <Typography component="span" role="status" aria-live="polite" sx={{
        position: "absolute", width: 1, height: 1, p: 0, m: -1,
        overflow: "hidden", clip: "rect(0, 0, 0, 0)", whiteSpace: "nowrap", border: 0,
      }}>
        {ordenacao.anuncio || (buscaVinculados.trim() ? 'Limpe a busca para reorganizar a lista completa.' : '')}
      </Typography>
      <TableContainer
        sx={{
          border: "1px solid var(--sear-borda, #d7e7f7)",
          borderRadius: 1.1,
          // O card termina antes da barra; somente a área interna rola.
          display: "flex", flexDirection: "column",
          flex: { sm: "1 1 0" }, minHeight: 0, minWidth: 0,
          width: "auto", mr: "var(--espaco-rolagem-documentos, 19px)",
          boxSizing: "border-box", overflow: "visible",
        }}
      >
        <Box
          ref={tabelaDocumentosRef}
          sx={{
            flex: { sm: "1 1 0" }, minHeight: 0, minWidth: 0,
            width: "calc(100% + var(--espaco-rolagem-documentos, 19px))",
            maxHeight: { xs: 420, sm: "none" },
            overflow: "auto", scrollbarGutter: "stable",
            pr: 0.5, boxSizing: "border-box",
          }}
        >
        <Table size="small" stickyHeader sx={{
          minWidth: 420,
          "& thead th:first-of-type": { borderTopLeftRadius: 1 },
          "& thead th:last-of-type": { borderTopRightRadius: 1 },
        }}>
          <TableHead>
            <TableRow>
              <TableCell sx={estiloCabecalhoTabela}>Documento</TableCell>

              <TableCell
                align="center"
                sx={{
                  ...estiloCabecalhoTabela,
                  width: 106,
                }}
              >
                Ações
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {documentosVinculados.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={2}
                  align="center"
                  sx={{
                    py: 2.6,
                    color: "var(--sear-texto-secundario, #71849d)",
                    fontSize: ajustarTamanhoFonteSre("0.77rem"),
                  }}
                >
                  <DescriptionRounded
                    sx={{
                      display: "block",
                      mx: "auto",
                      mb: 0.4,
                      opacity: 0.45,
                    }}
                  />

                  {processo.documentos.length === 0
                    ? "Nenhum documento adicionado."
                    : "Nenhum documento corresponde à busca."}
                </TableCell>
              </TableRow>
            ) : (
              documentosVinculados.map((doc) => (
                <TableRow key={doc.id} hover {...ordenacao.propsLinha(doc.id)} sx={{ cursor: loading || buscaVinculados.trim() ? 'default' : 'grab' }}>
                  <TableCell sx={{ py: 0.65 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <IconButton size="small" {...ordenacao.propsAlca(doc.id)}><DragIndicatorRounded sx={{ fontSize: 18 }} /></IconButton>
                      <Box
                        sx={{
                          p: 0.55,
                          color: "var(--sear-azul, #087cf0)",
                          bgcolor: "var(--sear-superficie-azul, #eaf4ff)",
                          display: "grid",
                          placeItems: "center",
                          borderRadius: 0.7,
                        }}
                      >
                        <DescriptionRounded sx={{ fontSize: 17 }} />
                      </Box>

                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            color: "var(--sear-azul, #173f72)",
                            fontWeight: 700,
                            fontSize: ajustarTamanhoFonteSre("0.76rem"),
                          }}
                        >
                          {doc.nome}
                        </Typography>

                        <Typography
                          sx={{
                            color: "var(--sear-texto-secundario, #71849d)",
                            fontSize: ajustarTamanhoFonteSre("0.69rem"),
                            overflowWrap: "anywhere",
                          }}
                        >
                          {doc.descricao || "—"}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>

                  <TableCell align="center" sx={{ py: 0.65 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteOutlineRounded sx={{ fontSize: 15 }} />}
                      onClick={() => removerDocumento(doc.id)}
                      disabled={loading || ordenacao.arrastando}
                      sx={{
                        textTransform: "none",
                        fontSize: ajustarTamanhoFonteSre("0.72rem"),
                        minWidth: 0,
                        py: 0.2,
                      }}
                    >
                      Remover
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        </Box>
      </TableContainer>

      {errors.documentos && (
        <Typography
          role="alert"
          color="error"
          sx={{
            fontSize: ajustarTamanhoFonteSre("0.74rem"),
            mt: 0.65,
          }}
        >
          {errors.documentos}
        </Typography>
      )}

    </Box>
  );

  return (
    <>
      {/* MODAL PRINCIPAL */}

      <Dialog
        open
        fullWidth
        maxWidth="sm"
        onClose={solicitarFechamento}
        aria-labelledby="titulo-criar-processo"
        PaperProps={{
          sx: {
            width: "calc(100% - 20px)",
            maxWidth: abaCadastro === 1 ? "960px !important" : "670px !important",
            height: abaCadastro === 1 ? { xs: "auto", sm: "calc(100% - 24px)" } : "auto",
            ...(abaCadastro === 1 && { m: { sm: 1.5 } }),
            maxHeight: "calc(100% - 24px)",
            borderRadius: 1.7,
            border: "1px solid var(--sear-borda, #d7e7f7)",
            borderTop: "4px solid var(--sear-borda-forte, #087cf0)",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 1.7,
            py: 1.05,
            bgcolor: "var(--sear-superficie-azul, #f9fcff)",
            borderBottom: "1px solid var(--sear-borda, #e5edf5)",
            display: "flex",
            alignItems: "center",
            gap: 1.2,
          }}
        >
          <Box
            sx={{
              display: {
                xs: "none",
                sm: "grid",
              },
              placeItems: "center",
              width: 36,
              height: 36,
              bgcolor: "var(--sear-superficie-azul, #eaf4ff)",
              color: "var(--sear-azul, #087cf0)",
              borderRadius: 1,
            }}
          >
            <FolderCopy sx={{ fontSize: 22 }} />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              id="titulo-criar-processo"
              component="h2"
              sx={{
                color: "var(--sear-azul, #0767d8)",
                fontSize: ajustarTamanhoFonteSre("1.12rem"),
                fontWeight: 700,
                lineHeight: 1.25,
              }}
            >
              {editando ? "Editar Processo" : "Criar Novo Processo"}
            </Typography>

            <Typography
              sx={{
                color: "var(--sear-texto-secundario, #71849d)",
                fontSize: ajustarTamanhoFonteSre("0.71rem"),
                display: {
                  xs: "none",
                  sm: "block",
                },
              }}
            >
              {editando
                ? "Atualize as informações e os documentos do processo."
                : "Preencha as informações principais do processo."}
            </Typography>
          </Box>

          <IconButton
            size="small"
            aria-label="Fechar cadastro"
            disabled={loading}
            onClick={solicitarFechamento}
          >
            <CloseRounded sx={{ fontSize: 19 }} />
          </IconButton>
        </DialogTitle>

        {/* ABAS */}

        <Tabs
          value={abaCadastro}
          onChange={(_, novaAba) => setAbaCadastro(novaAba)}
          aria-label="Abas do cadastro de processo"
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            flexShrink: 0,
            minHeight: 40,
            px: 1.8,
            borderBottom: "1px solid var(--sear-borda, #e5edf5)",
            "& .MuiTab-root": {
              minHeight: 40,
              textTransform: "none",
              fontSize: ajustarTamanhoFonteSre("0.73rem"),
              py: 0.6,
              px: 1.3,
            },
            "& .MuiTabs-indicator": {
              bgcolor: "var(--sear-acao-azul, #087cf0)",
              height: 2,
            },
          }}
        >
          <Tab
            icon={<FolderCopy sx={{ fontSize: 15 }} />}
            iconPosition="start"
            label="Dados do processo"
            id="aba-criar-dados"
            aria-controls="painel-criar-dados"
          />

          <Tab
            icon={<DescriptionRounded sx={{ fontSize: 15 }} />}
            iconPosition="start"
            label="Documentos necessários"
            id="aba-criar-documentos"
            aria-controls="painel-criar-documentos"
          />
        </Tabs>

        <DialogContent
          sx={{
            px: 1.8,
            py: 1.2,
            bgcolor: "var(--sear-superficie, #fbfdff)",
            ...(abaCadastro === 1 && !carregando && !errorType && {
              display: { sm: "flex" }, flexDirection: "column", minHeight: 0,
              overflowY: { xs: "auto", sm: "hidden" },
            }),
          }}
        >
          {carregando ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                py: 5,
              }}
            >
              <CircularProgress size={32} />
            </Box>
          ) : errorType ? (
            <Alert
              severity={errorType === "permission" ? "error" : "warning"}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setErrorType(null);
                    setRecarregarDocumentos((n) => n + 1);
                  }}
                >
                  Tentar novamente
                </Button>
              }
            >
              {errorType === "permission"
                ? "Sem permissão para consultar os documentos."
                : "Não foi possível carregar os documentos."}
            </Alert>
          ) : abaCadastro === 0 ? (
            /* ABA 1 */
            <Box
              role="tabpanel"
              id="painel-criar-dados"
              aria-labelledby="aba-criar-dados"
            >
              <TituloSecao
                icon={<DescriptionRounded sx={{ fontSize: 19 }} />}
                titulo="Dados do processo"
                subtitulo="Preencha as informações principais do processo."
              />

              {EXIBIR_TIPO_INSTITUICAO && (
                <Box sx={{ mb: 1.4 }}>
                  <Typography
                    sx={{
                      color: "var(--sear-azul, #27456f)",
                      fontSize: ajustarTamanhoFonteSre("0.75rem"),
                      fontWeight: 700,
                      mb: 0.55,
                    }}
                  >
                    Tipo de instituição *{" "}
                    <Tooltip title="A categoria define quais tipos estarão disponíveis na criação do processo pelo usuário.">
                      <InfoOutlined
                        sx={{
                          color: "var(--sear-azul, #087cf0)",
                          fontSize: 14,
                          verticalAlign: "middle",
                        }}
                      />
                    </Tooltip>
                  </Typography>

                  {!tipoInstituicao && (
                    <Typography
                      sx={{
                        color: "var(--sear-texto-secundario, #71849d)",
                        fontSize: ajustarTamanhoFonteSre("0.69rem"),
                        mb: 0.5,
                      }}
                    >
                      Selecione Mantenedora ou Escola para classificar este tipo de processo.
                    </Typography>
                  )}

                  <RadioGroup
                    row
                    value={tipoInstituicao}
                    onChange={(e) => {
                      setTipoInstituicao(e.target.value);
                      setErrors((atual) => ({ ...atual, tipoInstituicao: "" }));
                    }}
                    aria-label="Tipo de instituição"
                    aria-required="true"
                    sx={{ gap: 0.9 }}
                  >
                    {[
                      {
                        valor: "mantenedora",
                        nome: "Mantenedora",
                        Icone: BusinessRounded,
                        legenda: "Processo vinculado a uma mantenedora.",
                      },
                      {
                        valor: "escola",
                        nome: "Escola",
                        Icone: SchoolRounded,
                        legenda: "Processo vinculado a uma escola.",
                      },
                    ].map(({ valor, nome, Icone, legenda }) => (
                      <FormControlLabel
                        key={valor}
                        value={valor}
                        control={<Radio size="small" />}
                        label={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.75,
                              minWidth: 0,
                            }}
                          >
                            <Box
                              sx={{
                                color: "var(--sear-azul, #087cf0)",
                                bgcolor: "var(--sear-superficie-azul, #eaf4ff)",
                                p: 0.4,
                                borderRadius: 0.6,
                                display: "grid",
                                placeItems: "center",
                              }}
                            >
                              <Icone
                                sx={{
                                  fontSize: 19,
                                }}
                              />
                            </Box>

                            <Box>
                              <Typography
                                sx={{
                                  color: "var(--sear-azul, #0767d8)",
                                  fontSize: ajustarTamanhoFonteSre("0.75rem"),
                                  fontWeight: 700,
                                }}
                              >
                                {nome}
                              </Typography>

                              <Typography
                                sx={{
                                  color: "var(--sear-texto-secundario, #71849d)",
                                  fontSize: ajustarTamanhoFonteSre("0.66rem"),
                                }}
                              >
                                {legenda}
                              </Typography>
                            </Box>
                          </Box>
                        }
                        sx={{
                          m: 0,
                          px: 0.8,
                          py: 0.55,
                          borderRadius: 1,
                          border:
                            tipoInstituicao === valor
                              ? "1px solid var(--sear-borda-forte, #087cf0)"
                              : "1px solid var(--sear-borda, #d7e7f7)",
                          bgcolor:
                            tipoInstituicao === valor ? "var(--sear-superficie-azul, #eef7ff)" : "var(--sear-superficie, #fff)",
                          flex: "1 1 225px",
                          minWidth: 0,
                          maxWidth: "100%",
                        }}
                      />
                    ))}
                  </RadioGroup>
                  {errors.tipoInstituicao && (
                    <Typography role="alert" color="error" variant="caption">
                      {errors.tipoInstituicao}
                    </Typography>
                  )}
                </Box>
              )}

              {/* NOME */}

              <Box sx={{ mb: 1.2 }}>
                <Typography
                  component="label"
                  htmlFor="nome-processo"
                  sx={{
                    display: "block",
                    color: "var(--sear-azul, #27456f)",
                    fontSize: ajustarTamanhoFonteSre("0.76rem"),
                    fontWeight: 700,
                    mb: 0.45,
                  }}
                >
                  Nome do processo{" "}
                  <Box component="span" sx={{ color: "var(--sear-erro-texto, #e53935)" }}>
                    *
                  </Box>
                </Typography>

                <TextField
                  id="nome-processo"
                  fullWidth
                  size="small"
                  name="nome"
                  placeholder="Digite o nome do processo..."
                  value={processo.nome}
                  onChange={handleChange}
                  error={!!errors.nome}
                  helperText={errors.nome}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <TitleRounded
                          sx={{
                            fontSize: 17,
                            color: "var(--sear-texto-secundario, #607995)",
                          }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {/* DESCRIÇÃO */}

              <Box>
                <Typography
                  component="label"
                  htmlFor="descricao-processo"
                  sx={{
                    display: "block",
                    color: "var(--sear-azul, #27456f)",
                    fontSize: ajustarTamanhoFonteSre("0.76rem"),
                    fontWeight: 700,
                    mb: 0.45,
                  }}
                >
                  Descrição{" "}
                  <Box component="span" sx={{ color: "var(--sear-erro-texto, #e53935)" }}>
                    *
                  </Box>
                </Typography>

                <TextField
                  id="descricao-processo"
                  fullWidth
                  size="small"
                  name="descricao"
                  placeholder="Descreva o processo em detalhes..."
                  multiline
                  minRows={3}
                  value={processo.descricao}
                  onChange={handleChange}
                  error={!!errors.descricao}
                  helperText={errors.descricao}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment
                        position="start"
                        sx={{
                          alignSelf: "flex-start",
                          mt: 0.4,
                        }}
                      >
                        <SubjectRounded
                          sx={{
                            fontSize: 17,
                            color: "var(--sear-texto-secundario, #607995)",
                          }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </Box>
          ) : (
            /* ABA 2 */
            <Box
              role="tabpanel"
              id="painel-criar-documentos"
              aria-labelledby="aba-criar-documentos"
              sx={{ display: { sm: "flex" }, flexDirection: "column", flex: "1 1 0", minHeight: 0 }}
            >
              <Box
                sx={{
                  ...estiloSecao,
                  p: 1,
                  mb: 1,
                  flexShrink: 0,
                }}
              >
                <Typography sx={{ color: "var(--sear-azul, #0767d8)", fontWeight: 700, fontSize: ajustarTamanhoFonteSre("0.86rem") }}>
                  Resumo do processo
                </Typography>

                <Box
                  sx={{
                    display: "grid",
                    gap: 0.8,
                    mt: 1,
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: EXIBIR_TIPO_INSTITUICAO
                        ? "1fr 1.45fr 0.9fr"
                        : "1fr 2fr",
                    },
                    p: 0.8,
                    bgcolor: "var(--sear-superficie-azul, #f2f8fe)",
                    border: "1px solid var(--sear-borda, #d7e7f7)",
                    borderRadius: 0.8,
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        color: "var(--sear-texto-secundario, #71849d)",
                        fontSize: ajustarTamanhoFonteSre("0.67rem"),
                      }}
                    >
                      Nome do processo
                    </Typography>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.6,
                      }}
                    >
                      <TitleRounded
                        sx={{
                          color: "var(--sear-texto-secundario, #607995)",
                          fontSize: 16,
                          flexShrink: 0,
                        }}
                      />

                      <Typography
                        sx={{
                          color: "var(--sear-azul, #173f72)",
                          fontWeight: 700,
                          fontSize: ajustarTamanhoFonteSre("0.74rem"),
                          overflowWrap: "anywhere",
                          display: "-webkit-box", WebkitBoxOrient: "vertical",
                          WebkitLineClamp: 2, overflow: "hidden",
                        }}
                        title={processo.nome || "Não informado"}
                      >
                        {processo.nome || "Não informado"}
                      </Typography>
                    </Box>
                  </Box>

                  <Box>
                    <Typography
                      sx={{
                        color: "var(--sear-texto-secundario, #71849d)",
                        fontSize: ajustarTamanhoFonteSre("0.67rem"),
                      }}
                    >
                      Descrição
                    </Typography>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 0.6,
                      }}
                    >
                      <SubjectRounded
                        sx={{
                          color: "var(--sear-texto-secundario, #607995)",
                          fontSize: 16,
                          flexShrink: 0,
                          mt: 0.1,
                        }}
                      />

                      <Typography
                        sx={{
                          color: "var(--sear-texto-secundario, #40546d)",
                          fontSize: ajustarTamanhoFonteSre("0.73rem"),
                          overflowWrap: "anywhere",
                          display: "-webkit-box", WebkitBoxOrient: "vertical",
                          WebkitLineClamp: 2, overflow: "hidden",
                        }}
                        title={processo.descricao || "Não informada"}
                      >
                        {processo.descricao || "Não informada"}
                      </Typography>
                    </Box>
                  </Box>

                  {EXIBIR_TIPO_INSTITUICAO && (
                    <Box>
                      <Typography
                        sx={{
                          color: "var(--sear-texto-secundario, #71849d)",
                          fontSize: ajustarTamanhoFonteSre("0.67rem"),
                        }}
                      >
                        Tipo de instituição
                      </Typography>

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.6,
                        }}
                      >
                        {tipoInstituicao === "mantenedora" ? (
                          <BusinessRounded
                            sx={{
                              color: "var(--sear-azul, #087cf0)",
                              fontSize: 17,
                              flexShrink: 0,
                            }}
                          />
                        ) : tipoInstituicao === "escola" ? (
                          <SchoolRounded
                            sx={{
                              color: "var(--sear-azul, #087cf0)",
                              fontSize: 17,
                              flexShrink: 0,
                            }}
                          />
                        ) : null}

                        <Typography
                          sx={{
                            color: "var(--sear-azul, #173f72)",
                            fontWeight: 700,
                            fontSize: ajustarTamanhoFonteSre("0.74rem"),
                          }}
                        >
                          {tipoInstituicao === "mantenedora"
                            ? "Mantenedora"
                            : tipoInstituicao === "escola"
                              ? "Escola"
                              : "Não definida"}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>

              {documentosTabela}
            </Box>
          )}
        </DialogContent>

        {/* RODAPÉ */}

        <DialogActions
          sx={{
            px: 1.8,
            py: 0.95,
            borderTop: "1px solid var(--sear-borda, #e5edf5)",
            justifyContent: "space-between",
            bgcolor: "var(--sear-superficie, #fff)",
          }}
        >
          <Button
            variant="outlined"
            startIcon={<CancelRounded />}
            onClick={solicitarFechamento}
            disabled={loading}
            sx={botaoCancelar}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            startIcon={
              loading ? (
                <CircularProgress size={15} color="inherit" />
              ) : (
                <SaveRounded />
              )
            }
            onClick={handleSave}
            disabled={
              loading ||
              ordenacao.arrastando ||
              carregando ||
              Boolean(errorType) ||
              !lerTipoInstituicao(tipoInstituicao) ||
              (editando && !dadosAlterados)
            }
            sx={botaoSalvar}
          >
            {loading
              ? "Salvando..."
              : editando
                ? "Salvar alterações"
                : "Criar processo"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* MODAL ADICIONAR DOCUMENTOS */}

      <Dialog
        open={modalDocumentosAberto}
        onClose={() => setModalDocumentosAberto(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 1.6,
            borderTop: "4px solid var(--sear-borda-forte, #087cf0)",
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 2,
            py: 1.35,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid var(--sear-borda, #e5edf5)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <DescriptionRounded
              sx={{
                color: "var(--sear-azul, #087cf0)",
                fontSize: 26,
              }}
            />

            <Box>
              <Typography
                sx={{
                  color: "var(--sear-azul, #0767d8)",
                  fontWeight: 700,
                  fontSize: ajustarTamanhoFonteSre("0.96rem"),
                }}
              >
                Adicionar documentos
              </Typography>

              <Typography
                sx={{
                  color: "var(--sear-texto-secundario, #71849d)",
                  fontSize: ajustarTamanhoFonteSre("0.71rem"),
                }}
              >
                Selecione os documentos que farão parte deste processo.
              </Typography>
            </Box>
          </Box>

          <IconButton
            size="small"
            aria-label="Fechar"
            onClick={() => setModalDocumentosAberto(false)}
          >
            <CloseRounded />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 2, py: 1.4 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Buscar documento..."
            value={buscaModal}
            onChange={(e) => setBuscaModal(e.target.value)}
            sx={{ mb: 1.4 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRounded
                    sx={{
                      color: "var(--sear-texto-secundario, #607995)",
                      fontSize: 17,
                    }}
                  />
                </InputAdornment>
              ),
            }}
          />

          <TableContainer
            sx={{
              maxHeight: 340,
              border: "1px solid var(--sear-borda, #d7e7f7)",
              borderRadius: 1,
            }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" sx={estiloCabecalhoTabela}>
                    <Checkbox
                      size="small"
                      checked={todosVisiveisMarcados}
                      indeterminate={
                        algumVisivelMarcado && !todosVisiveisMarcados
                      }
                      disabled={!elegiveisVisiveis.length}
                      inputProps={{
                        "aria-label":
                          "Selecionar documentos disponíveis nesta busca",
                      }}
                      onChange={(e) => alternarTodosVisiveis(e.target.checked)}
                    />
                  </TableCell>

                  <TableCell sx={estiloCabecalhoTabela}>Documento</TableCell>

                  <TableCell sx={estiloCabecalhoTabela}>Descrição</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {documentosModal.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={3}
                      align="center"
                      sx={{
                        py: 3,
                        color: "var(--sear-texto-secundario, #71849d)",
                      }}
                    >
                      Nenhum documento encontrado.
                    </TableCell>
                  </TableRow>
                ) : (
                  documentosModal.map((doc) => {
                    const jaVinculado = idsVinculados.has(String(doc.id));
                    const marcado = jaVinculado || estaMarcado(doc.id);

                    return (
                      <TableRow key={doc.id} hover selected={marcado}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={marcado}
                            disabled={jaVinculado}
                            inputProps={{
                              "aria-label": `Selecionar documento ${doc.nome}`,
                            }}
                            onChange={(e) =>
                              alternarDocumentoMarcado(doc.id, e.target.checked)
                            }
                          />
                        </TableCell>

                        <TableCell
                          sx={{
                            color: "var(--sear-azul, #173f72)",
                            fontSize: ajustarTamanhoFonteSre("0.77rem"),
                            fontWeight: 600,
                          }}
                        >
                          {doc.nome}
                          {jaVinculado && (
                            <Typography
                              component="span"
                              sx={{
                                ml: 0.5,
                                color: "var(--sear-texto-secundario, #60748d)",
                                fontSize: ajustarTamanhoFonteSre("0.68rem"),
                              }}
                            >
                              (já adicionado)
                            </Typography>
                          )}
                        </TableCell>

                        <TableCell
                          sx={{
                            color: "var(--sear-texto-secundario, #60748d)",
                            fontSize: ajustarTamanhoFonteSre("0.72rem"),
                          }}
                        >
                          {doc.descricao || "—"}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>

        <DialogActions
          sx={{
            px: 2,
            py: 1.1,
            borderTop: "1px solid var(--sear-borda, #e5edf5)",
            justifyContent: "space-between",
          }}
        >
          <Button
            variant="outlined"
            startIcon={<CancelRounded />}
            sx={botaoCancelar}
            onClick={() => setModalDocumentosAberto(false)}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            startIcon={<AddRounded />}
            sx={botaoAdicionar}
            disabled={!documentosMarcados.length}
            onClick={adicionarDocumentosSelecionados}
          >
            Adicionar selecionados ({documentosMarcados.length})
          </Button>
        </DialogActions>
      </Dialog>

      {/* CONFIRMAÇÃO DE DESCARTE */}

      <Dialog
        open={confirmarSaida}
        onClose={() => setConfirmarSaida(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Descartar alterações?</DialogTitle>

        <DialogContent>
          <Typography sx={{ fontSize: ajustarTamanhoFonteSre("0.84rem") }}>
            Os dados preenchidos e os documentos selecionados não serão salvos.
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setConfirmarSaida(false)} sx={botaoCancelar}>
            Continuar editando
          </Button>

          <Button variant="contained" color="error" onClick={fecharCadastro}>
            Descartar
          </Button>
        </DialogActions>
      </Dialog>

      {/* FEEDBACK */}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() =>
          setSnackbar((atual) => ({
            ...atual,
            open: false,
          }))
        }
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          severity={snackbar.severity}
          sx={{ width: "100%" }}
          onClose={() =>
            setSnackbar((atual) => ({
              ...atual,
              open: false,
            }))
          }
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
